<template>
	<BaseDialog
		:model-value="$props.dialogVisible"
		:title="$props.title"
		:max-width="$props.options?.width"
		:z-index="$props.options?.zIndex || 9000"
		:style="{ zIndex: $props.options?.zIndex || 9000 }"
		:content-class="$props.options?.contentClass"
		:full-height="$props.options?.fullHeight"
		:contained="$props.options?.contained"
		:opacity="$props.options?.opacity"
		:scrim="$props.options?.scrim"
		:persistent="$props.persistentDialog"
		:actions-layout="actionsLayout"
		@update:model-value="handleOutsideClick"
		@keydown.enter="onDialogEnterKeydown"
		@after-leave="onAfterLeave">
		<template #title="{ titleId }">
			<h5 v-if="$props.title || !$slots['dialog-header']" :id="titleId" v-test="'dialog-title'">{{ $props.title }}</h5>
			<slot name="dialog-header" :title-id="titleId">
				<!-- for more complex displays -->
			</slot>
		</template>
		<span v-html="sanitizedMessage"></span>
		<slot name="dialog-body">
			<!-- for more complex displays -->
		</slot>
		<template v-if="!$props.removeFooter" #actions>
			<v-btn
				v-if="!$props.removeCancelButton"
				id="cancel-action-btn"
				v-test="'dialog-cancel-button'"
				v-track="'dialog-cancel-button'"
				:class="$props.primaryCancelButton ? '' : 'white-bg'"
				rounded="xl"
				elevation="0"
				:prepend-icon="$props.customCancelIcon"
				:color="$props.primaryCancelButton ? 'primary' : 'grey'"
				:variant="$props.primaryCancelButton ? 'flat' : $props.customButton || $props.removeAgreeButton ? 'text' : 'outlined'"
				:disabled="$props.agreeLoading"
				@click="cancel">
				{{ $props.cancelText }}
			</v-btn>
			<v-spacer v-if="$props.customButton" />
			<v-btn
				v-if="$props.customButton && $props.customButtonBeforeAgree"
				id="custom-action-btn"
				v-test="'dialog-custom-button'"
				v-track="'dialog-custom-button'"
				:color="$props.customButtonColor"
				:variant="$props.customButtonVariant"
				class="flex-grow-0 flex-shrink-0"
				rounded="xl"
				elevation="0"
				:disabled="$props.disableCustomButton"
				@click="customAction">
				{{ $props.customText }}
			</v-btn>
			<v-btn
				v-if="!$props.removeAgreeButton"
				id="confirm-action-btn"
				v-test="'dialog-agree-button'"
				v-track="'dialog-agree-button'"
				rounded="xl"
				:prepend-icon="$props.customAgreeIcon"
				elevation="0"
				:color="$props.isAngry ? 'error' : 'primary'"
				variant="flat"
				class="flex-grow-0 flex-shrink-0"
				:disabled="$props.disableAgreeButton"
				:loading="$props.agreeLoading"
				@click="agree">
				{{ $props.agreeText }}
			</v-btn>
			<v-btn
				v-if="$props.customButton && !$props.customButtonBeforeAgree"
				id="custom-action-btn"
				v-test="'dialog-custom-button'"
				v-track="'dialog-custom-button'"
				:color="$props.customButtonColor"
				:variant="$props.customButtonVariant"
				class="flex-grow-0 flex-shrink-0"
				rounded="xl"
				elevation="0"
				:disabled="$props.disableCustomButton"
				@click="customAction">
				{{ $props.customText }}
			</v-btn>
		</template>
	</BaseDialog>
</template>

<script lang="ts" setup>
	import { nextTick, computed } from "vue";
	import BaseDialog, { type BaseDialogActionsLayout } from "@common-ui/components/BaseDialog.vue";
	import { blurActiveElement } from "@/helpers/utilities";
	import { sanitizeDialog } from "@/helpers/htmlSanitize";

	// The frame (sizing, scrolling body, scroll shadows, iOS tap-outside close, reopen handling)
	// lives in the shared BaseDialog. This component keeps admin's prop-driven API: the message,
	// the Cancel / agree / custom buttons, and what an outside click or Escape does.
	const props = defineProps({
		dialogVisible: {
			type: Boolean,
			default: false,
			required: true
		},
		title: {
			type: String
		},
		message: {
			type: String
		},
		options: {
			type: Object,
			default: () => ({
				width: "500px"
			})
		},
		isAngry: {
			type: Boolean,
			default: false
		},
		removeCancelButton: {
			type: Boolean,
			default: false
		},
		removeAgreeButton: {
			type: Boolean,
			default: false
		},
		agreeText: {
			type: String,
			default: "OK"
		},
		cancelText: {
			type: String,
			default: "Cancel"
		},
		disableAgreeButton: {
			type: Boolean,
			default: false
		},
		agreeLoading: {
			type: Boolean,
			default: false
		},
		persistentDialog: {
			type: Boolean,
			default: false
		},
		customButton: {
			type: Boolean,
			default: false
		},
		customText: {
			type: String,
			default: "Save As"
		},
		disableCustomButton: {
			type: Boolean,
			default: false
		},
		customOutsideClickFunction: {
			type: Function,
			default: null
		},
		customButtonVariant: {
			type: String,
			default: "flat"
		},
		customButtonColor: {
			type: String,
			default: "primary"
		},
		customButtonBeforeAgree: {
			type: Boolean,
			default: false
		},
		primaryCancelButton: {
			type: Boolean,
			default: false
		},
		customCancelIcon: {
			type: String,
			default: ""
		},
		customAgreeIcon: {
			type: String,
			default: ""
		},
		removeFooter: {
			type: Boolean,
			default: false
		}
	});
	const emit = defineEmits(["cancel", "agree", "customAction", "afterLeave"]);

	const sanitizedMessage = computed(() => sanitizeDialog(props.message ?? ""));

	const actionsLayout = computed<BaseDialogActionsLayout>(() => {
		if (props.customButton) return "between";
		if (!props.removeCancelButton && !props.removeAgreeButton) return "split";
		return "end";
	});

	// Runs for an outside click or Escape. BaseDialog already ignores both when the dialog is
	// persistent (LSP-17844); the guard here keeps that true if this is ever called another way.
	// Of the five components binding `:persistent-dialog="true"`, only MergeContactDialog and
	// SheetEditor depend on it: NoAccount and FormUpgradeBlockedDialog pass a no-op override and
	// WelcomeWizardDialog binds no @cancel listener. ConfirmDialog forwards the prop from its caller.
	const handleOutsideClick = () => {
		if (props.persistentDialog) return;
		(props.customOutsideClickFunction || cancel)();
	};
	const cancel = () => {
		emit("cancel");
		nextTick(() => {
			blurActiveElement();
		});
	};
	const agree = () => {
		emit("agree");
	};
	const customAction = () => {
		emit("customAction");
	};
	const onAfterLeave = () => {
		emit("afterLeave");
	};

	// LSP-16532: keep Enter from submitting/closing while typing in a form field, but let it
	// activate a focused action button (Cancel/Delete/etc.) — the previous blanket
	// `@keydown.enter.prevent` cancelled the button's synthesized click, so keyboard users could
	// tab to a button but never trigger it.
	const onDialogEnterKeydown = (event: KeyboardEvent) => {
		const target = event.target as HTMLElement | null;
		if (target?.closest("button")) {
			return;
		}
		event.preventDefault();
	};
</script>
